import { useState, useCallback, useRef } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { isAxiosError, type AxiosError } from 'axios';
import { Link as RouterLink, Navigate, useNavigate } from 'react-router-dom';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { datePickerLocaleText } from '@/utils/datePickerLocaleText';
import dayjs from 'dayjs';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import { EmptyStateMessage, Section, SectionCard, SectionHeader } from '@/components/common';
import { paths } from '@/routes/paths';
import { useAuthStore } from '@/stores/useAuthStore';
import { formatDate } from '@/utils/dateFormatter';
import {
  formatAppraisalStatusLabel as formatStatusLabel,
  getAppraisalStatusChipSx as getStatusChipSx,
} from './appraisalStatusChip';
import { useActiveAppraisalCycle } from '@/queries/useAppraisalCycles';
import {
  useTalentCommittees,
  useUpdateTc,
  useAddAppraisee,
  useRemoveAppraisee,
  useAddMembers,
  useRemoveMember,
  useImportTcs,
  useExportTemplate,
  useAppraiseeCandidates,
  useMemberCandidates,
} from '@/queries/useTalentCommittees';
import type {
  TalentCommittee,
  TalentCommitteeAppraisee,
  AppraiseeCandidate,
  MemberCandidate,
  ImportError,
} from '@/api/types/talentCommittee.types';

type SortDirection = 'asc' | 'desc';

interface AppraiseeColumn {
  id: keyof TalentCommitteeAppraisee | 'actions';
  label: string;
  sortable: boolean;
  minWidth?: number;
}

const appraiseeColumns: AppraiseeColumn[] = [
  { id: 'firstName', label: 'APPRAISEE', sortable: true, minWidth: 150 },
  { id: 'tenureInGrade', label: 'TENURE IN GRADE', sortable: true, minWidth: 120 },
  { id: 'geo', label: 'GEO', sortable: true, minWidth: 120 },
  { id: 'collatorName', label: 'COLLATOR', sortable: true, minWidth: 150 },
  { id: 'status', label: 'STATUS', sortable: true, minWidth: 120 },
  { id: 'actions', label: 'ACTIONS', sortable: false, minWidth: 60 },
];

function getAppraiseeFullName(a: TalentCommitteeAppraisee): string {
  return `${a.firstName} ${a.lastName}`;
}

function sortAppraisees(
  appraisees: TalentCommitteeAppraisee[],
  sortBy: string,
  sortDirection: SortDirection
): TalentCommitteeAppraisee[] {
  return [...appraisees].sort((a, b) => {
    let aVal: string;
    let bVal: string;

    switch (sortBy) {
      case 'firstName':
        aVal = `${a.firstName} ${a.lastName}`.toLowerCase();
        bVal = `${b.firstName} ${b.lastName}`.toLowerCase();
        break;
      case 'tenureInGrade':
        aVal = (a.tenureInGrade ?? '').toLowerCase();
        bVal = (b.tenureInGrade ?? '').toLowerCase();
        break;
      case 'geo':
        aVal = a.geo.toLowerCase();
        bVal = b.geo.toLowerCase();
        break;
      case 'collatorName':
        aVal = a.collatorName.toLowerCase();
        bVal = b.collatorName.toLowerCase();
        break;
      case 'status':
        aVal = a.status.toLowerCase();
        bVal = b.status.toLowerCase();
        break;
      default:
        return 0;
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
}

export function TalentCommitteesPage() {
  const navigate = useNavigate();
  const permissions = useAuthStore((state) => state.permissions);
  const hasAnyRole = useAuthStore((state) => state.hasAnyRole);
  const canAccess = hasAnyRole(['Staffing', 'HR']);
  const canEdit = useAuthStore((state) => state.hasRole('Staffing'));

  // All hooks must be called before any conditional returns
  const { data: activeCycle, isLoading: cycleLoading } = useActiveAppraisalCycle();
  const { data: talentCommittees, isLoading: tcsLoading } = useTalentCommittees();

  // Mutations
  const updateTcMutation = useUpdateTc();
  const addAppraiseeMutation = useAddAppraisee();
  const removeAppraiseeMutation = useRemoveAppraisee();
  const addMembersMutation = useAddMembers();
  const removeMemberMutation = useRemoveMember();
  const importTcsMutation = useImportTcs();
  const exportTemplateMutation = useExportTemplate();

  // Candidate data
  const { data: appraiseeCandidates } = useAppraiseeCandidates();
  const { data: memberCandidates } = useMemberCandidates();

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Import dialog state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importErrors, setImportErrors] = useState<ImportError[]>([]);
  const [importing, setImporting] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Inline name editing state per TC
  const [editingNameTcId, setEditingNameTcId] = useState<number | null>(null);
  const [editingNameValue, setEditingNameValue] = useState('');

  // Remove appraisee confirmation dialog state
  const [removeConfirm, setRemoveConfirm] = useState<{
    open: boolean;
    tcId: number;
    tcName: string;
    appraisalId: number;
    appraiseeName: string;
  } | null>(null);

  // Kebab menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuContext, setMenuContext] = useState<{
    tcId: number;
    tcName: string;
    appraisalId: number;
    appraiseeName: string;
  } | null>(null);

  // Sort state per TC card (keyed by TC id)
  const [sortState, setSortState] = useState<Record<number, { sortBy: string; sortDirection: SortDirection }>>({});

  // Add appraisee row state (TC id that has the inline add row open)
  const [addingAppraiseeTcId, setAddingAppraiseeTcId] = useState<number | null>(null);
  const addAppraiseeInputRef = useRef<HTMLInputElement>(null);

  const handleSort = useCallback((tcId: number, column: string) => {
    setSortState((prev) => {
      const current = prev[tcId] || { sortBy: 'firstName', sortDirection: 'asc' as SortDirection };
      const isAsc = current.sortBy === column && current.sortDirection === 'asc';
      return {
        ...prev,
        [tcId]: { sortBy: column, sortDirection: isAsc ? 'desc' : 'asc' },
      };
    });
  }, []);

  // Redirect if user doesn't have required roles
  if (permissions && !canAccess) {
    return <Navigate to={paths.unauthorized} replace />;
  }

  // Inline name editing handlers
  const handleStartEditName = (tc: TalentCommittee) => {
    setEditingNameTcId(tc.id);
    setEditingNameValue(tc.name);
  };

  const handleCancelEditName = () => {
    setEditingNameTcId(null);
    setEditingNameValue('');
  };

  const handleSaveName = async (tc: TalentCommittee) => {
    if (!editingNameValue.trim()) return;
    if (editingNameValue.trim() === tc.name) {
      handleCancelEditName();
      return;
    }
    try {
      await updateTcMutation.mutateAsync({
        id: tc.id,
        request: { name: editingNameValue.trim(), plannedDate: tc.plannedDate },
        rowVersion: tc.rowVersion,
      });
      setSnackbar({ open: true, message: 'TC name updated successfully.', severity: 'success' });
      handleCancelEditName();
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 409) {
        handleCancelEditName();
        return;
      }
      const axiosError = error as AxiosError<{ message: string }>;
      const apiMessage = axiosError?.response?.data?.message ?? 'Failed to update TC name.';
      setSnackbar({ open: true, message: apiMessage, severity: 'error' });
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent, tc: TalentCommittee) => {
    if (e.key === 'Enter') {
      handleSaveName(tc);
    } else if (e.key === 'Escape') {
      handleCancelEditName();
    }
  };

  // Date change handler
  const handleDateChange = async (tc: TalentCommittee, value: dayjs.Dayjs | null) => {
    try {
      await updateTcMutation.mutateAsync({
        id: tc.id,
        request: { name: tc.name, plannedDate: value?.toISOString() ?? null },
        rowVersion: tc.rowVersion,
      });
      setSnackbar({ open: true, message: 'Planned date updated successfully.', severity: 'success' });
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 409) return;
      const axiosError = error as AxiosError<{ message: string }>;
      const apiMessage = axiosError?.response?.data?.message ?? 'Failed to update planned date.';
      setSnackbar({ open: true, message: apiMessage, severity: 'error' });
    }
  };

  // Members change handler
  const handleMembersChange = async (tc: TalentCommittee, newMembers: MemberCandidate[]) => {
    const currentStaffIds = tc.members.map((m) => m.staffId);
    const newStaffIds = newMembers.map((m) => m.staffId);

    const toAdd = newStaffIds.filter((id) => !currentStaffIds.includes(id));
    const toRemove = currentStaffIds.filter((id) => !newStaffIds.includes(id));

    try {
      if (toAdd.length > 0) {
        await addMembersMutation.mutateAsync({ tcId: tc.id, request: { staffIds: toAdd } });
      }
      for (const staffId of toRemove) {
        await removeMemberMutation.mutateAsync({ tcId: tc.id, staffId });
      }
      setSnackbar({ open: true, message: 'Members updated successfully.', severity: 'success' });
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const apiMessage = axiosError?.response?.data?.message ?? 'Failed to update members.';
      setSnackbar({ open: true, message: apiMessage, severity: 'error' });
    }
  };

  // Add appraisee handler
  const handleAddAppraisee = async (tcId: number, candidate: AppraiseeCandidate | null) => {
    if (!candidate) return;
    try {
      await addAppraiseeMutation.mutateAsync({ tcId, request: { appraisalId: candidate.id } });
      setAddingAppraiseeTcId(null);
      setSnackbar({
        open: true,
        message: `${candidate.firstName} ${candidate.lastName} added successfully.`,
        severity: 'success',
      });
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const apiMessage = axiosError?.response?.data?.message ?? 'Failed to add appraisee.';
      setSnackbar({ open: true, message: apiMessage, severity: 'error' });
    }
  };

  // Remove appraisee handler
  const handleRemoveAppraisee = async () => {
    if (!removeConfirm) return;
    try {
      await removeAppraiseeMutation.mutateAsync({
        tcId: removeConfirm.tcId,
        appraisalId: removeConfirm.appraisalId,
      });
      setSnackbar({
        open: true,
        message: `${removeConfirm.appraiseeName} removed successfully.`,
        severity: 'success',
      });
      setRemoveConfirm(null);
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const apiMessage = axiosError?.response?.data?.message ?? 'Failed to remove appraisee.';
      setSnackbar({ open: true, message: apiMessage, severity: 'error' });
      setRemoveConfirm(null);
    }
  };

  // Import handlers
  const handleImportDialogOpen = () => {
    setImportFile(null);
    setImportErrors([]);
    setImporting(false);
    setIsDragOver(false);
    setImportDialogOpen(true);
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportErrors([]);
    try {
      await importTcsMutation.mutateAsync(importFile);
      setSnackbar({ open: true, message: 'Talent committees imported successfully.', severity: 'success' });
      setImportDialogOpen(false);
    } catch (error) {
      const axiosError = error as AxiosError<{ success: boolean; importedCount: number; errors: ImportError[] }>;
      const responseErrors = axiosError?.response?.data?.errors;
      if (responseErrors && responseErrors.length > 0) {
        setImportErrors(responseErrors);
      } else {
        const message = (error as AxiosError<{ message: string }>)?.response?.data?.message ?? 'Import failed.';
        setSnackbar({ open: true, message, severity: 'error' });
        setImportDialogOpen(false);
      }
    } finally {
      setImporting(false);
    }
  };

  const handleExportTemplate = async () => {
    try {
      const blob = await exportTemplateMutation.mutateAsync();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'tc-template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setSnackbar({ open: true, message: 'Failed to download template.', severity: 'error' });
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.name.endsWith('.xlsx')) {
      setImportFile(file);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImportFile(file);
    }
  };

  // Kebab menu handlers
  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    tcId: number,
    tcName: string,
    appraisalId: number,
    appraiseeName: string
  ) => {
    setMenuAnchor(event.currentTarget);
    setMenuContext({ tcId, tcName, appraisalId, appraiseeName });
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuContext(null);
  };

  const handleRemoveClick = () => {
    if (menuContext) {
      setRemoveConfirm({
        open: true,
        tcId: menuContext.tcId,
        tcName: menuContext.tcName,
        appraisalId: menuContext.appraisalId,
        appraiseeName: menuContext.appraiseeName,
      });
    }
    handleMenuClose();
  };

  const isLoading = cycleLoading || tcsLoading;
  const tcs = talentCommittees ?? [];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} localeText={datePickerLocaleText}>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Talent Committees
          </Typography>
          {canEdit && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<UploadFileIcon />}
                onClick={handleImportDialogOpen}
              >
                Create TC via Import
              </Button>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<DownloadIcon />}
                onClick={handleExportTemplate}
              >
                Download TC Template
              </Button>
            </Box>
          )}
        </Box>

        {!activeCycle && (
          <EmptyStateMessage variant="inline" message="No active appraisal cycle." />
        )}

        {activeCycle && (
          <Section>
            <SectionHeader>
              Talent Committees - {activeCycle.name}
            </SectionHeader>

            {tcs.length === 0 && (
              <SectionCard>
                <EmptyStateMessage
                  variant="inline"
                  message="No talent committees found. Import a TC template to get started."
                />
              </SectionCard>
            )}

            {tcs.map((tc) => {
              const tcSort = sortState[tc.id] || { sortBy: 'firstName', sortDirection: 'asc' as SortDirection };
              const sortedAppraisees = sortAppraisees(tc.appraisees, tcSort.sortBy, tcSort.sortDirection);
              const existingAppraisalIds = tc.appraisees.map((a) => a.appraisalId);
              const filteredAppraiseeCandidates = (appraiseeCandidates ?? []).filter(
                (c) => !existingAppraisalIds.includes(c.id)
              );
              const currentMemberValues: MemberCandidate[] = tc.members.map((m) => ({
                staffId: m.staffId,
                firstName: m.firstName,
                lastName: m.lastName,
                billingGrade: m.billingGrade,
              }));

              return (
                <SectionCard key={tc.id}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
                    {canEdit ? (
                      editingNameTcId === tc.id ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TextField
                            size="small"
                            value={editingNameValue}
                            onChange={(e) => setEditingNameValue(e.target.value)}
                            onKeyDown={(e) => handleNameKeyDown(e, tc)}
                            onBlur={() => handleSaveName(tc)}
                            autoFocus
                            slotProps={{
                              htmlInput: { 'aria-label': 'TC name', maxLength: 25 },
                              formHelperText: {
                                sx: {
                                  position: 'absolute',
                                  top: '100%',
                                  left: 0,
                                  m: 0,
                                  lineHeight: 1,
                                },
                              },
                            }}
                            helperText={`${editingNameValue.length}/25`}
                            sx={{ position: 'relative' }}
                          />
                          <IconButton
                            size="small"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={handleCancelEditName}
                            aria-label="Cancel editing TC name"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Link
                            component="button"
                            variant="h6"
                            underline="hover"
                            onClick={() =>
                              navigate(paths.staffingActivities.talentCommitteeRun, {
                                state: { tcId: tc.id, tcName: tc.name },
                              })
                            }
                          >
                            {tc.name}
                          </Link>
                          <IconButton
                            size="small"
                            onClick={() => handleStartEditName(tc)}
                            aria-label="Edit TC name"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )
                    ) : (
                      <Link
                        component="button"
                        variant="h6"
                        underline="hover"
                        onClick={() =>
                          navigate(paths.staffingActivities.talentCommitteeRun, {
                            state: { tcId: tc.id, tcName: tc.name },
                          })
                        }
                      >
                        {tc.name}
                      </Link>
                    )}

                    {canEdit ? (
                      <DatePicker
                        value={tc.plannedDate ? dayjs(tc.plannedDate) : null}
                        onChange={(value) => handleDateChange(tc, value)}
                        format="DD MMM YYYY"
                        slotProps={{ textField: { size: 'small', sx: { width: 160 }, 'aria-label': 'Planned date' } }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(tc.plannedDate)}
                      </Typography>
                    )}

                    {canEdit ? (
                      <Autocomplete
                        multiple
                        size="small"
                        sx={{ minWidth: 300, flex: 1 }}
                        options={memberCandidates ?? []}
                        value={currentMemberValues}
                        getOptionLabel={(option) =>
                          `${option.firstName} ${option.lastName} (${option.billingGrade})`
                        }
                        isOptionEqualToValue={(option, value) => option.staffId === value.staffId}
                        onChange={(_, newValue) => handleMembersChange(tc, newValue)}
                        renderTags={(value, getTagProps) =>
                          value.map((member, index) => {
                            const { key, ...tagProps } = getTagProps({ index });
                            return (
                              <Chip
                                key={key}
                                label={`${member.firstName} ${member.lastName} (${member.billingGrade})`}
                                size="small"
                                {...tagProps}
                              />
                            );
                          })
                        }
                        renderInput={(params) => (
                          <TextField {...params} placeholder="TC Members" aria-label="TC Members" />
                        )}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {tc.members.map((member) => (
                          <Chip
                            key={member.staffId}
                            label={`${member.firstName} ${member.lastName} (${member.billingGrade})`}
                            size="small"
                          />
                        ))}
                      </Box>
                    )}

                    {canEdit && (
                      <Box sx={{ ml: 'auto' }}>
                        <Button
                          variant="contained"
                          color="secondary"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            if (addingAppraiseeTcId === tc.id) {
                              addAppraiseeInputRef.current?.focus();
                            } else {
                              setAddingAppraiseeTcId(tc.id);
                            }
                          }}
                        >
                          Add Appraisee
                        </Button>
                      </Box>
                    )}
                  </Box>

                  {(tc.appraisees.length > 0 || addingAppraiseeTcId === tc.id) && (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {appraiseeColumns.map((col) => {
                              if (col.id === 'actions' && !canEdit) return null;
                              return (
                                <TableCell key={col.id} sx={{ minWidth: col.minWidth }}>
                                  {col.sortable ? (
                                    <TableSortLabel
                                      active={tcSort.sortBy === col.id}
                                      direction={tcSort.sortBy === col.id ? tcSort.sortDirection : 'asc'}
                                      onClick={() => handleSort(tc.id, col.id)}
                                    >
                                      {col.label}
                                    </TableSortLabel>
                                  ) : (
                                    col.label
                                  )}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {addingAppraiseeTcId === tc.id && (
                            <TableRow sx={{ bgcolor: 'action.hover' }}>
                              <TableCell>
                                <Autocomplete
                                  size="small"
                                  options={filteredAppraiseeCandidates}
                                  getOptionLabel={(option) =>
                                    `${option.firstName} ${option.lastName} (${option.tenureInGrade ?? 'N/A'})`
                                  }
                                  onChange={(_, value) => handleAddAppraisee(tc.id, value)}
                                  value={null}
                                  openOnFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Escape') setAddingAppraiseeTcId(null);
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      inputRef={addAppraiseeInputRef}
                                      placeholder="Search by name..."
                                      aria-label="Search appraisee"
                                      autoFocus
                                    />
                                  )}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.disabled">-</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.disabled">-</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.disabled">-</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.disabled">-</Typography>
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={() => setAddingAppraiseeTcId(null)}
                                  aria-label="Cancel adding appraisee"
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          )}
                          {sortedAppraisees.map((appraisee) => {
                            const fullName = getAppraiseeFullName(appraisee);
                            return (
                              <TableRow key={appraisee.appraisalId}>
                                <TableCell>
                                  <Link
                                    component={RouterLink}
                                    to={`/appraisals/${appraisee.appraisalId}`}
                                    underline="hover"
                                  >
                                    {fullName}
                                  </Link>
                                </TableCell>
                                <TableCell>{appraisee.tenureInGrade ?? '-'}</TableCell>
                                <TableCell>{appraisee.geo}</TableCell>
                                <TableCell>{appraisee.collatorName}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={formatStatusLabel(appraisee.status)}
                                    size="small"
                                    sx={{
                                      minWidth: 100,
                                      justifyContent: 'center',
                                      ...getStatusChipSx(appraisee.status),
                                    }}
                                  />
                                </TableCell>
                                {canEdit && (
                                  <TableCell>
                                    <IconButton
                                      size="small"
                                      onClick={(e) =>
                                        handleMenuOpen(e, tc.id, tc.name, appraisee.appraisalId, fullName)
                                      }
                                      aria-label={`Actions for ${fullName}`}
                                    >
                                      <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </SectionCard>
              );
            })}
          </Section>
        )}

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleRemoveClick}>Remove</MenuItem>
        </Menu>

        <Dialog
          open={removeConfirm !== null}
          onClose={() => setRemoveConfirm(null)}
        >
          <DialogTitle>Remove Appraisee</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Remove {removeConfirm?.appraiseeName} from {removeConfirm?.tcName}?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRemoveConfirm(null)}>Cancel</Button>
            <Button onClick={handleRemoveAppraisee} color="error" variant="contained">
              Remove
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Import Talent Committees</DialogTitle>
          <DialogContent>
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                border: '2px dashed',
                borderColor: isDragOver ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                bgcolor: isDragOver ? 'action.hover' : 'transparent',
                cursor: 'pointer',
                mb: 2,
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              {importing ? (
                <CircularProgress />
              ) : importFile ? (
                <Typography>{importFile.name}</Typography>
              ) : (
                <Typography color="text.secondary">
                  Drag and drop an .xlsx file here, or click to select
                </Typography>
              )}
            </Box>

            {importErrors.length > 0 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Import errors:
                </Typography>
                {importErrors.map((err, idx) => (
                  <Typography key={idx} variant="body2">
                    Row {err.row}: {err.field} &quot;{err.value}&quot; - {err.reason}
                  </Typography>
                ))}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleImport}
              variant="contained"
              disabled={!importFile || importing}
            >
              Import
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
}

export default TalentCommitteesPage;
